import React from 'react';
import { Field, Image, ImageField, withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
import { ImageFieldValue } from '@sitecore-jss/sitecore-jss-react/types/components/Image';
import isEmpty from 'lodash-es/isEmpty';

import { ComponentProps, useFallbackTranslation } from '@gn-audio/jss-components';

import { JabraGallerySlider, JabraGallerySliderItem } from 'common/components/JabraGalerySlider';

type SliderImage = {
  identifier: string;
  image: {
    jsonValue: {
      value: {
        src: string;
        alt?: string;
      };
    };
  };
};

type PillSliderProps = ComponentProps & {
  fields: {
    data?: {
      item: {
        backgroundColor: Field<string>;
        condensedMenuAnchor?: Field<string>;
        condensedMenuEnabled?: Field<boolean>;
        condensedMenuTitle: Field<string>;
        enableContainer: {
          boolValue: boolean;
        };
        images: {
          results: SliderImage[];
        };
      };
    };
    backgroundColor?: Field<string>;
    images?: { image: { value: ImageFieldValue }; id: string }[];
  };
};

type SliderImageProps = {
  item: ImageSlide;
  index?: number;
  active?: boolean;
  onClick?: () => void;
};

type ImageSlide = {
  identifier: string;
  imageField: ImageField;
};
const SliderImage = ({ item, index = 0, active = false }: SliderImageProps) => (
  <JabraGallerySliderItem
    active={active}
    index={index}
    className="w-9/12 shrink-0 grow-0 basis-9/12 p-4 md:basis-1/2 lg:w-1/3 lg:basis-1/3 2xl:w-1/4 2xl:basis-1/4 [&:nth-child(3n+2)]:mt-20"
  >
    <div className="w-full overflow-hidden rounded-full">
      <Image field={item.imageField} width="100%" height="100%" loading="lazy" />
    </div>
  </JabraGallerySliderItem>
);

const PillSlider: React.FC<PillSliderProps> = ({ fields }) => {
  const { t } = useFallbackTranslation();
  const { item: queryFields } = fields?.data ?? {};
  const finalFields = queryFields ?? fields;

  if (!fields || !finalFields) return null;

  let slides: ImageSlide[];

  if (queryFields) {
    slides = queryFields.images.results.map((image) => ({
      identifier: image.image.jsonValue.value.src,
      imageField: image.image.jsonValue,
    }));
  } else {
    slides = (fields.images ?? []).map((image) => ({
      identifier: image.id,
      imageField: image.image,
    }));
  }
  const allImagesHaveAltText = slides.every((itm) => !isEmpty(itm.imageField.value?.alt));

  return (
    <div
      className="container-fluid"
      role="group"
      aria-label={allImagesHaveAltText ? `${t('system.ImageSlider')}` : undefined}
      style={{ background: finalFields.backgroundColor?.value }}
    >
      <JabraGallerySlider
        items={slides}
        itemComponent={SliderImage}
        itemsAmountStep={1}
        showButtons="lg"
        oneItem="max-lg"
        loop="max-lg"
        enableContainerForGallery={queryFields?.enableContainer?.boolValue}
      />
    </div>
  );
};

export default withDatasourceCheck()(PillSlider);
